// src/encounter/intelligence-rail.tsx
//
// PROD-2743 · frame v2. The encounter's right column: Scrips Intelligence ONLY (D-31), drawn to the approved round 18r
// design (`Round 5 - Intelligence rail.dc.html`, frame `encounter-visit`, [data-rail]):
//   · a 32px header — the ONE live Orb of the surface (DS-042), "Scrips Intelligence" 13/600, "· <phase>" in
//     text-secondary (Pre-visit · Visit · Wrap-up), and the "More" button (the design gives it no menu: disabled);
//   · a scrolling column of bordered cards (the host's children);
//   · a footer pinned at the bottom (the host's `footer`: IntelligenceRailFooter).
// The rail sits inside the DS ContextRail (the frame's column, border and scroll container); ContextRail's own
// centred header is not used (no title is passed), so the design's left-aligned header is drawn here.
//
// The Orb's state is the host's to give, from what Scrips Intelligence is actually doing
// (`orbStateForAmbient`). Never a constant (design desk, #569).
import { forwardRef, useContext, type ReactNode } from 'react';
import { ContextRail, Icon, OrbIndicator, type OrbState } from '@scripsteam/scrips-signal-ds';
import { X } from 'lucide-react';
import { R } from './intelligence/rail-classes';
import type { IntelligencePhase } from './intelligence/rail-words';
import { IntelligenceCloseContext } from './intelligence/rail-close';

export interface IntelligenceRailProps {
  orbState: OrbState;
  phase: IntelligencePhase;
  /** The scrolling column of cards. */
  children: ReactNode;
  /** Pinned at the bottom, under the column. */
  footer?: ReactNode;
}

export function IntelligenceRail({ orbState, phase, children, footer }: IntelligenceRailProps) {
  // PROD-3388: the screen's close (rail-close.ts). No close given, no X: the rail stays as it was.
  const onClose = useContext(IntelligenceCloseContext);
  return (
    <ContextRail width="fill" ariaLabel="Scrips Intelligence">
      <div data-slot="intelligence-rail" data-rail className={R.rail}>
        <div data-slot="intelligence-header" className={R.header}>
          <OrbIndicator variant="orb" state={orbState} size={20} />
          <span data-slot="intelligence-title" className={R.title}>
            Scrips Intelligence
          </span>
          <span data-slot="intelligence-phase" className={R.phase}>
            {`· ${phase}`}
          </span>
          {/* The design draws "More" with no menu behind it: shown, not operable. */}
          <button type="button" data-slot="intelligence-more" className={R.iconButton} aria-label="More" title="More" disabled>
            <Icon name="more" size={16} aria-hidden />
          </button>
          {onClose ? (
            <button
              type="button"
              data-slot="intelligence-close"
              className={R.close}
              aria-label="Close Intelligence"
              title="Close Intelligence"
              onClick={onClose}
            >
              <X size={16} aria-hidden />
            </button>
          ) : null}
        </div>
        <div data-slot="intelligence-body" className={R.body}>
          {children}
        </div>
        {footer}
      </div>
    </ContextRail>
  );
}

/**
 * PROD-2962 (OL-1307) · the COLLAPSED rail's content (EncounterShell `trailingRail`, D-41): the ONE live Orb
 * of the surface with what the ambient scribe is doing, so its status stays visible while the rail is
 * closed. The shell draws the 56px rail and the expand chevron.
 */
export function IntelligenceStrip({ orbState, statusWord }: { orbState: OrbState; statusWord?: string }) {
  const label = statusWord ? `Scrips Intelligence — ${statusWord}` : 'Scrips Intelligence';
  return (
    <span data-slot="intelligence-strip" role="img" aria-label={label} title={label} className="flex flex-col items-center gap-1">
      <OrbIndicator variant="orb" state={orbState} size={24} />
      {statusWord ? (
        <span data-slot="intelligence-strip-status" aria-hidden className="text-size-xs leading-3.5 text-text-secondary text-center">
          {statusWord}
        </span>
      ) : null}
    </span>
  );
}

/**
 * PROD-3388 · the closed rail: the prototype's floating round button (IntelligenceFab), bottom right, holding the strip's
 * ONE live Orb. It reopens the rail. The same button on the pre-visit and on the started encounter (bridge `intel-fab`).
 */
export const IntelligenceFab = forwardRef<HTMLButtonElement, { onOpen: () => void; children: ReactNode }>(function IntelligenceFab(
  { onOpen, children },
  ref,
) {
  return (
    <button ref={ref} type="button" data-slot="intel-fab" aria-label="Open Scrips Intelligence" onClick={onOpen}>
      {children}
    </button>
  );
});
